import { Card, Grid, LinearProgress, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateComponent, DateVariant } from "components/base/DateComponent";
import { Section } from "components/disw";
import ErrorBox from "components/error/ErrorBox";
import React from "react";
import { useHistory } from "react-router-dom";
import { createRoute } from "utils/url";
import { useRateAgreementById } from "./hooks/useRateAgreementById";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2)
  },
  card: {
    width: 667,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    boxShadow: "0px 1px 2px 0px #0000004D"
  },
  propertyGrid: {
    minWidth: 112,
    maxWidth: 112,
    width: 112,
    flexBasis: 112,
    flexShrink: 0,
    flexGrow: 0,
    paddingRight: theme.spacing(1)
  },
  property: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#1E1E1E"
  },
  value: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#464646",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  rateTableCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    boxShadow: "0px 1px 2px 0px #0000004D",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#EEF6F8" }
  },
  rateTableName: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    color: "#1E1E1E"
  },
  rateTableMeta: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "11px",
    color: "#464646"
  },
  structureChip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 3,
    fontSize: "11px",
    fontFamily: "Segoe UI",
    backgroundColor: "#EEF6F8",
    color: "#1E1E1E",
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  }
}));

interface PropertyRowProps {
  label: string;
  children: React.ReactNode;
  testId?: string;
}

function PropertyRow({ label, children, testId }: PropertyRowProps) {
  const classes = useStyles();
  return (
    <Grid container wrap="nowrap" alignItems="flex-start">
      <Grid item className={classes.propertyGrid}>
        <Typography className={classes.property}>{label}</Typography>
      </Grid>
      <Grid item xs>
        <Typography
          className={classes.value}
          component="div"
          data-testid={testId}
        >
          {children}
        </Typography>
      </Grid>
    </Grid>
  );
}

interface RateAgreementDetailsProps {
  agreementId: string;
}

export default function RateAgreementDetails({
  agreementId
}: RateAgreementDetailsProps) {
  const classes = useStyles();
  const history = useHistory();
  const { rateAgreement, loading, error } = useRateAgreementById(agreementId);

  if (loading) return <LinearProgress />;
  if (error) return <ErrorBox apolloError={error} />;
  if (!rateAgreement) return null;

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <Section name="Agreement Details">
          <PropertyRow label="Name" testId="detail-name">
            {rateAgreement.name}
          </PropertyRow>
          <PropertyRow label="Description" testId="detail-description">
            {rateAgreement.description ?? "\u2014"}
          </PropertyRow>
          <PropertyRow label="Carrier" testId="detail-carrier">
            {rateAgreement.carrier}
          </PropertyRow>
          <PropertyRow label="Service Method" testId="detail-service-method">
            {rateAgreement.serviceMethod}
          </PropertyRow>
        </Section>

        <Section name="Rate Tables">
          {rateAgreement.rateTables.map(rt => (
            <div
              key={rt.id}
              className={classes.rateTableCard}
              data-testid={`rate-table-card-${rt.id}`}
              role="button"
              tabIndex={0}
              onClick={() =>
                history.push(
                  createRoute(`/rate-agreements/${agreementId}/${rt.id}`)
                )
              }
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  history.push(
                    createRoute(`/rate-agreements/${agreementId}/${rt.id}`)
                  );
                }
              }}
            >
              <Typography className={classes.rateTableName}>
                {rt.type} {"\u2014"} {rt.currency}
              </Typography>
              <Typography className={classes.rateTableMeta}>
                {rt.description ?? "No description"}
              </Typography>
              <Grid container spacing={2}>
                <Grid item>
                  <Typography className={classes.rateTableMeta}>
                    <strong>Valid From:</strong>{" "}
                    <DateComponent
                      date={rt.validFrom}
                      variant={DateVariant.SHORT}
                    />
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography className={classes.rateTableMeta}>
                    <strong>Valid To:</strong>{" "}
                    <DateComponent
                      date={rt.validTo}
                      variant={DateVariant.SHORT}
                    />
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography className={classes.rateTableMeta}>
                    <strong>Min. Procedure:</strong>{" "}
                    {rt.useMinimumProcedure ? "Yes" : "No"}
                  </Typography>
                </Grid>
              </Grid>
              <div style={{ marginTop: 4 }}>
                {rt.rateStructure.pickupLocationFilters.map((f, i) => (
                  <span key={i} className={classes.structureChip}>
                    Pickup: {f.column}
                  </span>
                ))}
                {rt.rateStructure.deliveryLocationFilters.map((f, i) => (
                  <span key={i} className={classes.structureChip}>
                    Delivery: {f.column}
                  </span>
                ))}
                {rt.rateStructure.serviceOptionFilters.map((f, i) => (
                  <span key={i} className={classes.structureChip}>
                    Option: {f.column}
                  </span>
                ))}
                <span className={classes.structureChip}>
                  {rt.rateStructure.primaryQuantity.column} (
                  {rt.rateStructure.primaryQuantity.unit})
                </span>
                {rt.rateStructure.additionalQuantities.map((q, i) => (
                  <span key={i} className={classes.structureChip}>
                    {q.column} ({q.unit})
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Section>
      </Card>
    </div>
  );
}
